import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchWorkflowByUuid, saveOrUpdateWorkflow, deleteWorkflow } from '../../api/WorkflowApi';
import './WorkflowPage.scss';
import { Workflow, WorkflowStatus } from '../model';
import { PageProps } from '../main/App';
import WorkflowEditor from './editor/WorkflowEditor';

const emptyWorkflow: Workflow = {
  name: '',
  status: WorkflowStatus.INACTIVE,
  definition: '',
};

const useWorkflowData = (uuid?: string) => {
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [originalWorkflow, setOriginalWorkflow] = useState<Workflow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isNewWorkflow = !uuid;

  useEffect(() => {
    if (!isNewWorkflow) {
      setLoading(true);
      fetchWorkflowByUuid(uuid)
        .then(data => {
          setWorkflow(data);
          setOriginalWorkflow(data);
        })
        .catch(() => setError('Failed to load workflow'))
        .finally(() => setLoading(false));
    } else {
      setWorkflow(emptyWorkflow);
      setOriginalWorkflow(emptyWorkflow);
    }
  }, [uuid, isNewWorkflow]);

  return { workflow, setWorkflow, originalWorkflow, setOriginalWorkflow, error, loading, isNewWorkflow };
};

const WorkflowPage: React.FC<PageProps> = ({ showNotification }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const {
    workflow,
    setWorkflow,
    originalWorkflow,
    setOriginalWorkflow,
    error,
    loading,
    isNewWorkflow,
  } = useWorkflowData(uuid);
  const [nameError, setNameError] = useState<string | null>(null);

  const isWorkflowChanged = JSON.stringify(workflow) !== JSON.stringify(originalWorkflow);

  const handleSaveWorkflow = useCallback(async () => {
    if (!workflow) return;
    if (!workflow.name.trim()) {
      setNameError('Name is required');
      return;
    }
    setNameError(null);
    try {
      const saved = await saveOrUpdateWorkflow(workflow);
      setWorkflow(saved);
      setOriginalWorkflow(saved);
      navigate(`/workflow/${saved.uuid}`);
    } catch {
      showNotification('Failed to save workflow');
    }
  }, [workflow, setWorkflow, setOriginalWorkflow, navigate, showNotification]);

  const handleDelete = useCallback(async () => {
    if (!workflow) return;
    try {
      await deleteWorkflow(workflow.uuid!);
      navigate('/workflows');
    } catch {
      showNotification('Failed to delete workflow');
    }
  }, [workflow, navigate, showNotification]);

  const getStatusBadge = (status?: WorkflowStatus) => {
    switch (status) {
      case WorkflowStatus.ACTIVE:
        return <span className="status-badge active">Active</span>;
      case WorkflowStatus.INACTIVE:
        return <span className="status-badge inactive">Inactive</span>;
      default:
        return <span className="status-badge default">Unknown</span>;
    }
  };

  const setDefinition = (newDefinition: any) => {
    setWorkflow(prevWorkflow => {
      if (!prevWorkflow) return null;
      return {
        ...prevWorkflow,
        definition: newDefinition,
      };
    });
  };

  if (workflow === null) return <div>Loading...</div>;

  return (
    <div className="workflow-page">
      <div className="header-container">
        <h1>{isNewWorkflow ? 'Create New Workflow' : 'Workflow Details'}</h1>
        {getStatusBadge(workflow.status)}
        <button className="save-button" onClick={handleSaveWorkflow} disabled={!isWorkflowChanged || loading || !workflow.name.trim()}>
          {loading ? 'Saving...' : 'Save Workflow'}
        </button>
        {!isNewWorkflow && (
          <button className="delete-button" onClick={handleDelete} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete Workflow'}
          </button>
        )}
      </div>
      {error && <div className="error-message">{error}</div>}
      <div className="input-group">
        <label>
          Name:
          <input
            className="input-field"
            type="text"
            value={workflow?.name || ''}
            onChange={e => {
              setWorkflow({ ...workflow, name: e.target.value });
              if (nameError && e.target.value.trim()) setNameError(null);
            }}
          />
        </label>
        {nameError && <div className="error-message">{nameError}</div>}
      </div>
      <WorkflowEditor
        isNewWorkflow={isNewWorkflow}
        definition={workflow.definition}
        setDefinition={setDefinition}
      />
    </div>
  );
};

export default WorkflowPage;
